import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useGetRequestsQuery, useGetInventoriesQuery } from "../api/starterKitApi";
import { useSelector } from "react-redux";
import { selectUserRole, selectCurrentUser } from "../../../core/data/slice/authSlice";
import CountUp from "react-countup";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import CreateRequestModal from "../components/CreateRequestModal";
import UpdateRequestStatusModal from "../components/UpdateRequestStatusModal";
import DeleteRequestModal from "../components/DeleteRequestModal";
import ViewRequestModal from "../components/ViewRequestModal";
import AddInventoryModal from "../components/AddInventoryModal";
import AddStockModal from "../components/AddStockModal";
import UpdateMainCenterStockModal from "../components/UpdateMainCenterStockModal";
import dayjs from "dayjs";

const StarterKitRequestList = () => {
  const routes = all_routes;
  const userRole = useSelector(selectUserRole);
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [requestToView, setRequestToView] = useState<any>(null);
  const [requestToDelete, setRequestToDelete] = useState<number | null>(null);

  const currentUser = useSelector(selectCurrentUser);

  const { data: inventoryData, isLoading: isInventoriesLoading } = useGetInventoriesQuery(
    userRole === "superadmin"
      ? { is_main_center_stock: 1 }
      : { center_id: currentUser?.center_id, is_main_center_stock: 0 }
  );
  const currentStocks = inventoryData?.data?.items || [];
  const totalAvailable = currentStocks.reduce((sum: number, item: any) => sum + item.available_stock, 0);
  const totalStock = currentStocks.reduce((sum: number, item: any) => sum + item.total_stock, 0);

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();

  const hasAllMainCenterStock = courses.length > 0 && courses.every((course: any) => 
    currentStocks.some((stock: any) => 
      Number(stock.course_id) === Number(course.id) || Number(stock.course?.id) === Number(course.id)
    )
  );

  const { data: requestData, isLoading, isError } = useGetRequestsQuery({
    page,
    size: recordsPerPage,
  });

  const requests = requestData?.data?.items || [];
  const totalItems = requestData?.data?.totalItems || 0;

  const handlePageChange = (p: number, pageSize?: number) => {
    setPage(p);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPage(1);
  };

  const columns: any[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Course",
      render: (record: any) => record?.course?.name || "N/A",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Requested On",
      render: (record: any) => record.requested_on ? dayjs(record.requested_on).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "Status",
      render: (record: any) => {
        const color = record.status === 'approved' ? 'success' : record.status === 'rejected' ? 'danger' : 'warning';
        const formattedStatus = record.status ? record.status.charAt(0).toUpperCase() + record.status.slice(1) : "Unknown";
        return (
          <span className={`badge badge-soft-${color} d-inline-flex align-items-center`}>
            <i className="ti ti-circle-filled fs-5 me-1"></i>
            {formattedStatus}
          </span>
        );
      }
    }
  ];

  if (userRole === "superadmin") {
    columns.splice(1, 0, {
      title: "Center",
      render: (record: any) => record?.center?.name || "N/A",
    });
  }

  columns.push({
    title: "Action",
    render: (_: any, record: any) => {
      return (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#view_request_modal"
                  onClick={() => setRequestToView(record)}
                >
                  <i className="ti ti-eye me-2" />
                  View Details
                </Link>
              </li>
              {record.status === 'pending' && userRole === "superadmin" && (
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#update_request_status_modal"
                    onClick={() => setSelectedRequest(record)}
                  >
                    <i className="ti ti-edit me-2" />
                    Review Request
                  </Link>
                </li>
              )}
              {record.status === 'pending' && userRole === "centeradmin" && (
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#delete_request_modal"
                    onClick={() => setRequestToDelete(record.id)}
                  >
                    <i className="ti ti-trash me-2" />
                    Delete Request
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      );
    }
  });

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Starter Kit Requests</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Inventory</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Requests
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            {userRole === "centeradmin" && (
              <div className="mb-2 me-2">
                <Link
                  to="#"
                  className="btn btn-primary d-flex align-items-center"
                  data-bs-toggle="modal"
                  data-bs-target="#create_request_modal"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  New Request
                </Link>
              </div>
            )}
            {userRole === "superadmin" && (
              <>
                {!isInventoriesLoading && !hasAllMainCenterStock && (
                  <div className="mb-2 me-2">
                    <Link
                      to="#"
                      className="btn btn-primary d-flex align-items-center"
                      data-bs-toggle="modal"
                      data-bs-target="#add_inventory"
                    >
                      <i className="ti ti-square-rounded-plus me-2" />
                      Add Initial Stock
                    </Link>
                  </div>
                )}
                {currentStocks.length > 0 && (
                  <>
                    <div className="mb-2 me-2">
                      <Link
                        to="#"
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#add_stock"
                      >
                        <i className="ti ti-square-rounded-plus me-2" />
                        Add More Stock
                      </Link>
                    </div>
                    <div className="mb-2 me-2">
                      <Link
                        to="#"
                        className="btn btn-warning d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#update_main_center_stock"
                      >
                        <i className="ti ti-edit me-2" />
                        Update Stock (Manual)
                      </Link>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-xxl-4 col-md-4 col-sm-12 d-flex">
            <div className="card flex-fill animate-card border-0">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-xl me-2 bg-primary-transparent p-1">
                    <i className="ti ti-package fs-24 text-primary"></i>
                  </div>
                  <div className="overflow-hidden flex-fill">
                    <div className="d-flex align-items-center justify-content-between">
                      <h2>
                        <CountUp end={totalStock} />
                      </h2>
                    </div>
                    <p>Overall Total Stock</p>
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                  <span>Available: {totalAvailable}</span>
                  <span>Distributed: {totalStock - totalAvailable}</span>
                </div>
              </div>
            </div>
          </div>
          {courses.map((course: any) => {
            const stock = currentStocks.find((s: any) =>
              Number(s.course_id) === Number(course.id) || Number(s.course?.id) === Number(course.id)
            );
            const courseTotal = stock?.total_stock || 0;
            const courseAvailable = stock?.available_stock || 0;
            const courseDistributed = courseTotal - courseAvailable;
            
            return (
              <div className="col-xxl-4 col-md-4 col-sm-12 d-flex" key={course.id}>
                <div className="card flex-fill animate-card border-0">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="avatar avatar-xl me-2 bg-success-transparent p-1">
                        <i className="ti ti-books fs-24 text-success"></i>
                      </div>
                      <div className="overflow-hidden flex-fill">
                        <div className="d-flex align-items-center justify-content-between">
                          <h2>
                            <CountUp end={courseTotal} />
                          </h2>
                        </div>
                        <p>{course.name || 'Unknown Course'} Stock</p>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                      <span>Available: {courseAvailable}</span>
                      <span>Distributed: {courseDistributed}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Request List</h4>
          </div>
          <div className="card-body p-0 py-3">
            {isLoading ? (
              <div className="text-center">Loading requests...</div>
            ) : isError ? (
              <div className="alert alert-danger m-3">
                Error loading requests.
              </div>
            ) : (
              <Table
                dataSource={requests}
                columns={columns}
                Selection={false}
                onSearchChange={handleSearchChange}
                pagination={{
                  current: page,
                  pageSize: recordsPerPage,
                  total: totalItems,
                  onChange: handlePageChange,
                  pageSizeOptions: ["All", 10, 20, 30],
                }}
              />
            )}
          </div>
        </div>
      </div>
      <CreateRequestModal />
      <ViewRequestModal initialData={requestToView} onClose={() => setRequestToView(null)} />
      <UpdateRequestStatusModal initialData={selectedRequest} onClose={() => setSelectedRequest(null)} />
      <DeleteRequestModal id={requestToDelete} onClose={() => setRequestToDelete(null)} />
      {userRole === "superadmin" && (
        <>
          <AddInventoryModal />
          <AddStockModal />
          <UpdateMainCenterStockModal />
        </>
      )}
    </div>
  );
};

export default StarterKitRequestList;
